import React from 'react';
import { PermissionGroupProps } from '../types';
import {
  createTestId,
  getResourceAndScope,
  hasSpecialResources,
  getActionDisplayText,
} from '../utils';
import PermissionRow from './PermissionRow';

/**
 * Component for rendering a permission group
 */
const PermissionGroup: React.FC<PermissionGroupProps> = ({
  group,
  groupApplicableActions,
  resourceMap,
  selectedPermissions,
  onSelectPermission,
}) => {
  const testId = createTestId('permission-group', group.group);
  const headerTestId = createTestId('group-header', group.group);

  const hasSpecialResourcesFlag = hasSpecialResources(group);

  return (
    <div className="border border-neutral-200 rounded-lg" data-testid={testId}>
      {/* Group Title */}
      <div
        className="px-4 py-3 bg-neutral-50 border-b border-neutral-200"
        data-testid={headerTestId}
      >
        <div className="flex flex-col">
          <div className="text-sm font-semibold text-neutral-700">
            {group.group}
          </div>
          {group.permissions.some((permission) => {
            const { resource } = getResourceAndScope(permission.code);
            return resource === 'internal_banner';
          }) && (
            <div className="text-xs text-neutral-500">
              Show &quot;Internal Use Only&quot; banner
            </div>
          )}
          {group.permissions.some((permission) => {
            const { resource } = getResourceAndScope(permission.code);
            return resource === 'reimbursement';
          }) && (
            <div className="text-xs text-neutral-500">
              Show reimbursement data on profiles
            </div>
          )}
        </div>
      </div>

      {/* Group-specific Header Row */}
      {!hasSpecialResourcesFlag && (
        <div
          className="flex items-center px-4 py-2 bg-white border-b border-neutral-200"
          data-testid={createTestId('group-actions-header', group.group)}
        >
          <div className="w-64 text-sm font-semibold text-neutral-500">
            Resources
          </div>
          <div className="flex flex-1">
            {groupApplicableActions.map((action) => (
              <div
                key={action}
                data-testid={`header-action-${group.group
                  .toLowerCase()
                  .replace(/\s+/g, '-')}-${action}`}
                className="flex-1 flex text-xs font-medium text-neutral-500 justify-end pr-4"
              >
                {getActionDisplayText(action)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Group Permissions */}
      <div
        className="divide-y divide-neutral-100"
        data-testid={createTestId('group-permissions', group.group)}
      >
        {Object.entries(resourceMap).map(([resource, scopeMap]) => (
          <div key={resource} className="py-2">
            {Object.entries(scopeMap).map(([scope, permissions]) => (
              <PermissionRow
                key={`${resource}-${scope}`}
                resource={resource}
                scope={scope}
                permissions={permissions}
                groupApplicableActions={groupApplicableActions}
                selectedPermissions={selectedPermissions}
                onSelectPermission={onSelectPermission}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PermissionGroup;
